import React, { useId, useState } from "react";

interface PeakTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "text" | "email" | "password";
  autoComplete?: string;
  autoCapitalize?: "none" | "words" | "sentences" | "characters";
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  multiline?: boolean;
  minHeight?: number;
  error?: boolean;
  disabled?: boolean;
  /** Small status rendered inside the field on the right (username availability). */
  trailing?: React.ReactNode;
  onSubmit?: () => void;
  name?: string;
}

/**
 * Peak Design System "Text Field" (node 66:2) as used by iOS `PeakTextField`:
 * 60px box, radius 8, bg-secondary, 1px border-default → 1.5px text-primary on
 * focus (error red), floating Funnel Medium 11 label, Funnel SemiBold 15 value.
 */
const PeakTextField: React.FC<PeakTextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  autoComplete,
  autoCapitalize,
  inputMode,
  multiline = false,
  minHeight = 96,
  error = false,
  disabled = false,
  trailing,
  onSubmit,
  name,
}) => {
  const id = useId();
  const [focused, setFocused] = useState(false);
  const borderClass = error
    ? "border-peak-error"
    : focused
      ? "border-peak-text"
      : "border-peak-border";

  const inputClass =
    "w-full bg-transparent font-funnel text-[15px] font-semibold text-peak-text outline-none placeholder:font-normal placeholder:text-peak-text-tertiary disabled:opacity-60";

  return (
    <label
      htmlFor={id}
      className={`relative flex w-full flex-col justify-center rounded-lg bg-peak-bg-secondary px-4 transition-[border-color,border-width] ${borderClass}`}
      style={{ borderStyle: "solid", borderWidth: focused || error ? 1.5 : 1, minHeight: multiline ? minHeight : 60 }}
    >
      <span className="pt-2.5 font-funnel text-[11px] font-medium leading-none text-peak-text-tertiary">{label}</span>
      <div className="flex items-center gap-2 pb-2.5 pt-1.5">
        {multiline ? (
          <textarea
            id={id}
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            disabled={disabled}
            rows={3}
            className={`${inputClass} resize-none`}
          />
        ) : (
          <input
            id={id}
            name={name}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && onSubmit) {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder={placeholder}
            autoComplete={autoComplete}
            autoCapitalize={autoCapitalize}
            inputMode={inputMode}
            disabled={disabled}
            className={inputClass}
          />
        )}
        {trailing && <span className="shrink-0 font-funnel text-[12px] font-medium">{trailing}</span>}
      </div>
    </label>
  );
};

export default PeakTextField;
